import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import React from 'react';

import { type ImportReportData, costLine } from './importReportData';
import './structured/structured.scss';

/**
 * The structured generator's own account of what it produced, and what it cost.
 *
 * Problems are the rules it could not satisfy on its retry; notes are the gaps it chose not to fill
 * (NEVER INVENT says it was right) and the safety nets it applied. Both are things only the operator
 * can judge, and neither is visible in the guide itself.
 */
const ImportReport: React.FC<{ report: ImportReportData; onDismiss?: () => void }> = ({ report, onDismiss }) => (
  <Box className="ge-report" data-testid="import-report">
    {report.problems.length > 0 && (
      <Alert
        severity="warning"
        icon={<ReportProblemOutlinedIcon fontSize="small" />}
        onClose={onDismiss}
        sx={{ borderRadius: 'var(--border-radius-lg)' }}
      >
        <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
          {report.problems.length} thing(s) to fix before publishing
        </AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The generator retried once and could not resolve these. Fix them in the fields below.
        </Typography>
        <Box component="ul" className="ge-report__list">
          {report.problems.map((p, i) => (
            <Typography key={i} component="li" variant="body2">
              {p}
            </Typography>
          ))}
        </Box>
      </Alert>
    )}
    {report.notes.length > 0 && (
      <Alert severity="info" sx={{ borderRadius: 'var(--border-radius-lg)' }}>
        <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
          {report.notes.length} note(s) from the generator
        </AlertTitle>
        <Box component="ul" className="ge-report__list">
          {report.notes.map((p, i) => (
            <Typography key={i} component="li" variant="body2">
              {p}
            </Typography>
          ))}
        </Box>
      </Alert>
    )}
    {report.placementsRemoved.length > 0 && (
      <Alert severity="info" sx={{ borderRadius: 'var(--border-radius-lg)' }}>
        <AlertTitle sx={{ fontWeight: 'var(--font-weight-semibold)' }}>
          {report.placementsRemoved.length} image placement(s) removed
        </AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The model placed these, and they were taken out because they were not from your document,
          repeated the banner, or appeared twice. Your document&apos;s own images are all still in
          the guide.
        </Typography>
        <Box component="ul" className="ge-report__list">
          {report.placementsRemoved.map((url) => (
            <Typography key={url} component="li" variant="body2">
              <code>{url}</code>
            </Typography>
          ))}
        </Box>
      </Alert>
    )}
    <Typography className="ge-report__cost" data-testid="import-cost">
      {costLine(report.usage)}
    </Typography>
  </Box>
);

export default ImportReport;
